<template>
    <div class="choice" :class="[`choice--cols-${columns}`, { 'choice--compact': compact }]" role="group">
        <button
            v-for="(option, index) in parsedOptions"
            :key="index"
            type="button"
            class="choice-item"
            :class="{ 'is-active': selected == option.value }"
            :aria-pressed="selected == option.value"
            :style="{ '--choice-index': index }"
            @click="select(option.value)"
        >
            <span class="choice-pip" aria-hidden="true"><i class="fa fa-check"></i></span>
            <span class="choice-body">
                <span class="choice-title">
                    {{ option.title }}
                    <span v-if="option.badge" class="choice-badge">{{ option.badge }}</span>
                </span>
                <span v-if="option.subTitle" class="choice-sub">{{ option.subTitle }}</span>
            </span>
        </button>
    </div>
</template>

<script>
export default {
    name: "ChoiceList",
    props: {
        // Options shaped as { title, subTitle, value } - same shape the wizard
        // already uses for its option lists
        options: {
            type: Array,
            default: () => []
        },
        selected: [String, Boolean, Number],
        columns: {
            type: Number,
            default: 1
        },
        compact: {
            type: Boolean,
            default: false
        }
    },
    emits: ['select'],
    computed: {
        //Note: a trailing "(Recommended)" in the label is lifted out of the title
        //and shown as a chip - the underlying option value is untouched
        parsedOptions() {
            return this.options.map((option) => {
                const title = option.title || "";
                const match = title.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
                return {
                    ...option,
                    title: match ? match[1].trim() : title.trim(),
                    badge: match ? match[2] : null
                };
            });
        }
    },
    methods: {
        select(value) {
            // Re-selecting the active option clears it, so callers keep receiving
            // null on deselect exactly as before
            this.$emit('select', this.selected == value ? null : value);
        }
    }
}
</script>

<style scoped>
.choice {
    --choice-ink: #12263F;
    --choice-ink-soft: #6E84A3;
    --choice-ink-faint: #95AAC9;
    --choice-line: #E3EBF6;
    --choice-hover: #F9FBFD;
    --choice-accent: #2C7BE5;
    --choice-accent-ink: #1657B0;
    --choice-accent-wash: rgba(44, 123, 229, 0.07);
    --choice-ring: rgba(44, 123, 229, 0.16);

    display: grid;
    gap: 0.125rem;
    max-width: 44rem;
}

/* Two columns at modal width, three once the content column is wide enough -
   a dozen event types then fit without a long scroll on a large display */
.choice--cols-2 {
    grid-template-columns: repeat(auto-fit, minmax(17rem, 1fr));
    column-gap: 0.75rem;
    max-width: 100%;
}

/* A quiet menu rather than a field of cards: rows carry no chrome until they
   are hovered or chosen, so a long option list still reads as one block */
.choice-item {
    position: relative;
    display: flex;
    align-items: flex-start;
    gap: 0.6875rem;
    width: 100%;
    padding: 0.6875rem 0.875rem;
    text-align: left;
    background: none;
    border: 0;
    border-radius: 0.5rem;
    cursor: pointer;
    transition: background-color 0.15s ease, box-shadow 0.18s ease;
    animation: choice-in 0.3s cubic-bezier(0.16, 0.84, 0.44, 1) both;
    animation-delay: calc(var(--choice-index, 0) * 20ms);
}

.choice--compact .choice-item {
    padding: 0.5625rem 0.75rem;
}

.choice-item:hover {
    background-color: var(--choice-hover);
}

.choice-item:focus-visible {
    outline: 2px solid var(--choice-accent);
    outline-offset: 1px;
}

.choice-item.is-active {
    background-color: var(--choice-accent-wash);
    box-shadow: inset 0.125rem 0 0 var(--choice-accent), 0 0 0 1px var(--choice-ring);
}

.choice-pip {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 1.125rem;
    height: 1.125rem;
    margin-top: 0.0625rem;
    font-size: 0.5rem;
    color: transparent;
    background-color: transparent;
    border: 1px solid var(--choice-line);
    border-radius: 50%;
    transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease;
}

.choice-item:hover .choice-pip {
    border-color: var(--choice-ink-faint);
}

.choice-item.is-active .choice-pip {
    color: #ffffff;
    background-color: var(--choice-accent);
    border-color: var(--choice-accent);
}

.choice-body {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    min-width: 0;
}

.choice-title {
    display: flex;
    align-items: center;
    gap: 0.4375rem;
    font-size: 0.875rem;
    font-weight: 600;
    line-height: 1.35;
    letter-spacing: -0.005em;
    color: var(--choice-ink);
}

.choice--compact .choice-title {
    font-size: 0.8125rem;
}

.choice-item.is-active .choice-title {
    color: var(--choice-accent-ink);
}

.choice-badge {
    padding: 0.0625rem 0.375rem;
    font-size: 0.625rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--choice-accent-ink);
    background-color: var(--choice-accent-wash);
    border: 1px solid var(--choice-ring);
    border-radius: 0.75rem;
    white-space: nowrap;
}

.choice-sub {
    font-size: 0.75rem;
    line-height: 1.45;
    color: var(--choice-ink-soft);
}

.choice--compact .choice-sub {
    font-size: 0.6875rem;
}

@keyframes choice-in {
    from {
        opacity: 0;
        transform: translateY(5px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@media (max-width: 767.98px) {
    .choice--cols-2 {
        grid-template-columns: minmax(0, 1fr);
    }
}

@media (prefers-reduced-motion: reduce) {
    .choice-item {
        animation: none;
        transition: none;
    }
}
</style>

<style lang="scss">
@use '../../theme/scss/_user-variables.scss' as *;

body.dark-mode .choice {
    --choice-ink: #{$dark-text-primary};
    --choice-ink-soft: #{$dark-text-secondary};
    --choice-ink-faint: #{$dark-text-muted};
    --choice-line: #{$dark-border-color};
    --choice-hover: #{$dark-hover-bg};
    --choice-accent: #4d90ea;
    --choice-accent-ink: #a8caf8;
    --choice-accent-wash: rgba(77, 144, 234, 0.15);
    --choice-ring: rgba(77, 144, 234, 0.24);
}
</style>
